/**
 * HelpCenterPage — CMS-served help content.
 *
 * E2 spec:
 * - Top search bar (titles + body + video transcripts)
 * - Workflow buckets (Customers, Quotes, Jobs & WO, Calendar, Invoicing, Settings & Onboarding)
 * - Article pages with anchor-tagged outline (floating right side)
 * - Schedule-a-call card (HubSpot scheduler link)
 * - Submit Feedback → Jira form (existing link)
 * - Support email shown alongside scheduler
 *
 * Replaces existing hard-coded FAQ accordions with CMS-served content.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import PageHeader from '../ui/PageHeader';
import { MagnifyingGlassIcon, UsersIcon, ClipboardDocumentListIcon, WrenchScrewdriverIcon, CalendarDaysIcon, BanknotesIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { HELP_BUCKETS, helpCMSAPI } from './helpCMS';

const BUCKET_ICONS = {
  users: UsersIcon,
  'clipboard-list': ClipboardDocumentListIcon,
  wrench: WrenchScrewdriverIcon,
  calendar: CalendarDaysIcon,
  banknotes: BanknotesIcon,
  cog: Cog6ToothIcon,
};

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [articles, setArticles] = useState({});

  // Load articles per bucket
  useEffect(() => {
    async function loadArticles() {
      const byBucket = {};
      for (const bucket of HELP_BUCKETS) {
        byBucket[bucket.id] = await helpCMSAPI.getArticles(bucket.id);
      }
      setArticles(byBucket);
    }
    loadArticles();
  }, []);

  // Search with debounce
  useEffect(() => {
    if (!search.trim() || search.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      const results = await helpCMSAPI.searchArticles(search);
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="px-6 max-w-4xl mx-auto">
      <div className="mb-8 text-center">
        <PageHeader title="Help Center" subtitle="Find answers to common questions about BIDIT" />
      </div>

      {/* Search bar — searches titles + body + video transcripts */}
      <div className="relative mb-8">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search help articles, videos, and guides…"
          className="pl-10"
          aria-label="Search help articles"
        />
      </div>

      {/* Search results */}
      {search.trim() && (
        <div className="mb-8">
          {isSearching ? (
            <p className="text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>Searching…</p>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </p>
              {searchResults.map((article) => (
                <button
                  key={article.id}
                  type="button"
                  className="w-full text-left px-4 py-3 rounded-md border border-[color:var(--color-border)] transition-colors hover:border-red-200"
                  style={{ borderColor: 'var(--color-border)' }}
                  onClick={() => navigate(`/help/article/${article.id}`)}
                >
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{article.title}</p>
                  {article.excerpt && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>{article.excerpt}</p>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>
              No results for "{search}". Try different keywords.
            </p>
          )}
        </div>
      )}

      {/* Workflow buckets */}
      {!search.trim() && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {HELP_BUCKETS.map((bucket) => {
            const bucketArticles = articles[bucket.id] || [];
            return (
              <button
                key={bucket.id}
                type="button"
                className="text-left card p-4 transition-colors hover:border-red-200"
                style={{ borderColor: 'var(--color-border)' }}
                onClick={() => navigate(`/help/bucket/${bucket.id}`)}
              >
                <div className="flex items-center gap-2 mb-2">
                  {(() => {
                    const IconComp = BUCKET_ICONS[bucket.icon];
                    return IconComp ? <IconComp className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} /> : null;
                  })()}
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {bucket.label}
                  </h3>
                </div>
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {bucket.description}
                </p>
                {bucketArticles.length > 0 && (
                  <p className="text-xs mt-2" style={{ color: 'var(--color-action-primary)' }}>
                    {bucketArticles.length} article{bucketArticles.length !== 1 ? 's' : ''}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Support section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {/* Schedule-a-call card */}
        <Card className="p-4 space-y-2">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Schedule a Call
          </h3>
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Book a time with our support team for personalized help.
          </p>
          <a
            href="https://meetings.hubspot.com/bidit-support"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm font-medium mt-2"
            style={{ color: 'var(--color-action-primary)' }}
          >
            Open Scheduler →
          </a>
        </Card>

        {/* Submit Feedback + Support email */}
        <Card className="p-4 space-y-2">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Contact Support
          </h3>
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Email us or submit feedback through our form.
          </p>
          <div className="flex flex-col gap-1 mt-2">
            <a
              href="mailto:support@bidit.com"
              className="text-sm font-medium"
              style={{ color: 'var(--color-action-primary)' }}
            >
              support@bidit.com
            </a>
            <button
              type="button"
              onClick={() => navigate('/help/feedback')}
              className="text-sm font-medium text-left"
              style={{ color: 'var(--color-action-primary)' }}
            >
              Submit Feedback →
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
